import React, { useContext, useMemo, createContext, useReducer } from 'react';

import { EVENTS_HISTORY_RETENTION_DAYS, EventsContext, getInitialFilterState } from 'context/events';

import { useAspenLogger } from 'hooks/useAspenLogger';
import {
  adjustFilterRange,
  convertFilterStateToQueryStatesPayload,
  getAppliedFilters,
  getNumberOfActiveFilters,
} from 'modules/events';

import { LocalFilterState } from 'types/events';

interface EventsFilterContextState {
  filterState: LocalFilterState;
  selectedFilters: number;
}

interface EventsFilterContext extends EventsFilterContextState {
  applyFilter: () => void;
  resetFilter: () => void;
}

type EventsFilterActions =
  | {
      type: 'filterState';
      payload: Partial<LocalFilterState>;
    }
  | {
      type: 'selectedFilters';
      payload: number;
    };

const getInitialLocalFilterState = (): LocalFilterState => ({
  ...getInitialFilterState(),
  startTime: null,
  endTime: null,
  leaves: {},
  objectsDetected: [],
  order: 'desc',
  triggers: [],
});

const INITIAL_LOCAL_FILTER_STATE: LocalFilterState = getInitialLocalFilterState();

const writeFilterStateToSessionStorage = (filterState: LocalFilterState) => {
  // Save the current filter state to session storage
  sessionStorage.setItem('eventsFilterState', JSON.stringify(filterState));
  // Count the number of selected filters
  const appliedFilters = getAppliedFilters(filterState, INITIAL_LOCAL_FILTER_STATE);
  sessionStorage.setItem('eventsAppliedFilters', JSON.stringify(appliedFilters));
  const filterCount = getNumberOfActiveFilters(appliedFilters);
  sessionStorage.setItem('eventsFilterCount', filterCount.toString());

  // Dispatch custom event to notify badge component about filter count change
  window.dispatchEvent(
    new CustomEvent('eventsFilterCountChanged', {
      detail: { filterCount },
    }),
  );
};

export const EventsFilterContext = createContext<EventsFilterContext>({} as EventsFilterContext);

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const EventsFilterDispatchContext = createContext<React.Dispatch<EventsFilterActions>>(() => {});

export interface EventsFilterProviderProps {
  children: React.ReactNode;
}

const reducer = (state: EventsFilterContextState, action: EventsFilterActions | { type: 'reset' }) => {
  switch (action.type) {
    case 'filterState':
      return { ...state, filterState: { ...state.filterState, ...action.payload } };
    case 'selectedFilters':
      return { ...state, selectedFilters: action.payload };
    case 'reset':
      return getDefaultInitState();
    default:
      return state;
  }
};

const getDefaultInitState = (): EventsFilterContextState => ({
  filterState: getInitialLocalFilterState(),
  selectedFilters: 0,
});

const DEFAULT_INIT_STATE: EventsFilterContextState = getDefaultInitState();

export const EventsFilterProvider = ({ children }: EventsFilterProviderProps) => {
  const { setFilterState } = useContext(EventsContext);
  const { logAspenEvent } = useAspenLogger();

  const sessionStorageFilterState = sessionStorage.getItem('eventsFilterState');
  const initialFilterState =
    sessionStorageFilterState !== null ? JSON.parse(sessionStorageFilterState) : DEFAULT_INIT_STATE.filterState;

  const [{ filterState, selectedFilters }, dispatch] = useReducer(reducer, {
    ...DEFAULT_INIT_STATE,
    filterState: {
      ...initialFilterState,
    },
  });

  const applyFilter = () => {
    // Adjust and validate events filter range
    const adjustedFilterRange = adjustFilterRange({
      startTime: filterState.startTime,
      endTime: filterState.endTime,
      retentionDays: EVENTS_HISTORY_RETENTION_DAYS,
    });
    const adjustedFilter = {
      ...filterState,
      ...adjustedFilterRange,
    };
    // Set local filter state
    dispatch({ type: 'filterState', payload: filterState });
    // Write the filter state to session storage
    writeFilterStateToSessionStorage(filterState);
    // Update the filter state in the context
    const queryStatesPayload = convertFilterStateToQueryStatesPayload({
      filterState: adjustedFilter,
      retentionDays: EVENTS_HISTORY_RETENTION_DAYS,
    });
    setFilterState(queryStatesPayload);

    // Log the filter action
    logAspenEvent({
      log_id: 'iot.web.event.filter',
      payload: {
        action: 'apply',
        filter_payload: queryStatesPayload,
      },
    });
  };

  const resetFilter = () => {
    // Reset the local filter state to the initial state (calculated fresh)
    const freshInitialState = getInitialLocalFilterState();
    dispatch({ type: 'reset' });
    // Update the filter state in the context with initial values
    const queryStatesPayload = convertFilterStateToQueryStatesPayload({
      filterState: freshInitialState,
      retentionDays: EVENTS_HISTORY_RETENTION_DAYS,
    });
    setFilterState(queryStatesPayload);
    // Write the filter state to session storage with initial values
    writeFilterStateToSessionStorage(freshInitialState);

    // Log the filter action
    logAspenEvent({
      log_id: 'iot.web.event.filter',
      payload: {
        action: 'reset',
      },
    });
  };

  const contextValue = useMemo(
    () => ({
      filterState,
      selectedFilters,
      applyFilter,
      resetFilter,
    }),
    [filterState, selectedFilters, applyFilter, resetFilter],
  );

  return (
    <EventsFilterContext.Provider value={contextValue}>
      <EventsFilterDispatchContext.Provider value={dispatch}>{children}</EventsFilterDispatchContext.Provider>
    </EventsFilterContext.Provider>
  );
};
